import {React, useEffect} from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    Divider,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import LayersIcon from '@mui/icons-material/Layers';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import StormDateDropDown from '../Not-Currently-Used/StormDateDropDown';
import { useMapContext } from '../../contexts/MapContext';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createDevelopmentPlan, deleteDevelopmentPlan } from '../../graphql/mutations';
import { generateClient } from 'aws-amplify/api';
import outputs from "../../amplifyconfiguration.json";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {useLIDContext} from "../../contexts/LIDContext";

const client = generateClient({
    authMode: "userPool",
    region: outputs.aws_appsync_region,
    url: outputs.aws_appsync_graphqlEndpoint,
});


const TEMPLATE_DEV_PLANS = ["100Mil-HighImpervElev"];

const DevelopmentPlanSelector = () => {
    const {
        userData,
        devPlanID,
        setDevPlanID,
        setLIDMode,
        setUserData,
        setPlanIsRunning,
        colorScheme,
        setColorScheme,
        planRunStatus,
        setGeoJSONData,
        selectedStormOption,
        containPlan,
        setLoadingFloodMap,
        viewBaseline,
        geoJSONData,
    } = useMapContext();
    const {
        setDevPlanContent,
        devPlanContent,
    } = useLIDContext();

    const [isNewPlanModalOpen, setIsNewPlanModalOpen] = useState(false);
    const [isCopyPlanModalOpen, setIsCopyPlanModalOpen] = useState(false);
    const [newPlanName, setNewPlanName] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [nameError, setNameError] = useState('');
    const [newPlanLoading, setNewPlanLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');

    // const [isNewPlan, setIsNewPlan] = useState(false);


    const resetFlBaseFlValues = () => {
        // Reset flooding color map to black before fetching new data
        setGeoJSONData(prevData => ({
            ...prevData,
            subcatchments: {
                ...prevData.subcatchments,
                features: prevData.subcatchments?.features?.map(feature => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        avgFlooding: null,  // Black map during API call
                        basement_flooding: null   // Black basement flooding map during API call
                    }
                }))
            }
        }));
    }

    const handleChange = async (event) => {
        const selectedValue = event.target.value;
        setPlanIsRunning(false);
        if (selectedValue === 'create_new') {
            setIsNewPlanModalOpen(true);
        } else {
            console.log("% % % % Change to plan ID : ", selectedValue);
            setDevPlanID(selectedValue);
        }
    };

    const handleDeleteClick = (event, plan) => {
        event.stopPropagation();
        setPlanToDelete(plan);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!planToDelete) return;

        try {
            await client.graphql({
                query: deleteDevelopmentPlan,
                variables: { input: { id: planToDelete.id } }
            });

            await fetch('https://2471d2z813.execute-api.us-east-2.amazonaws.com/deleteDevPlan/' + userData.id + '/' + planToDelete.id, {
                method: 'DELETE',
            })
                .then(response => response.json())
                .then(data => {
                    console.log('Success:', data);
                })
                .catch((error) => {
                    console.error('Error:', error);
                });

            // Update local state
            setUserData(prevData => ({
                ...prevData,
                developmentPlans: {
                    ...prevData.developmentPlans,
                    items: prevData.developmentPlans.items.filter(plan => plan.id !== planToDelete.id)
                }
            }));

            // Kaming : setting the dev plan id to 10 gives a warning because it is not in the dropdown options.
            // Warning : You have provided an out-of-range value `10` for the select component.
            // Consider providing a value that matches one of the available options or ''.
            // The available values are `create_new`.
            if (devPlanID === planToDelete.id) {
                setDevPlanID('');
            }

        } catch (error) {
            console.error('Error deleting development plan:', error);
        }

        setIsDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const handleDeleteCancel = () => {
        setIsDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const handleCancelNewPlan = () => {
        setIsNewPlanModalOpen(false);
        setNewPlanName('');
        setNameError('');
    };

    const handleNewPlanNameChange = (e) => {
        const value = e.target.value;
        setNewPlanName(value);
        
        if (value.trim() === '') {
            setNameError('');
        } else if (userData?.developmentPlans?.items?.some(plan => 
            plan.name.toLowerCase() === value.trim().toLowerCase()
        )) {
            setNameError('A plan with this name already exists.');
        } else {
            setNameError('');
        }
    };

    const handleCreateNewPlan = async () => {
        if (!userData || !userData.id || !newPlanName.trim()) {
            console.error('Cannot create development plan: User ID is missing or plan name is empty');
            return;
        }
        try {
            // Check if the plan name already exists
            const existingPlanNames = userData.developmentPlans?.items?.map(plan => plan.name.toLowerCase()) || [];
            if (existingPlanNames.includes(newPlanName.trim().toLowerCase())) {
                return;
            }

            const newPlan = {
                id: uuidv4(),
                name: newPlanName.trim(),
                version: 1,
                userID: userData.id,
                progress_percent: -2
            };
            const result = await client.graphql({
                query: createDevelopmentPlan,
                variables: { input: newPlan }
            });

            console.log('Created new development plan:', result.data.createDevelopmentPlan);
            setPlanIsRunning(false);


            const createdPlan = result.data.createDevelopmentPlan;
            // Update local state
            setUserData(prevData => ({
                ...prevData,
                developmentPlans: {
                    ...prevData.developmentPlans,
                    items: [...prevData.developmentPlans.items, createdPlan]
                }
            }));

            setDevPlanID(createdPlan.id);
            console.log("The devPlanID is now: ", devPlanID);
            setIsNewPlanModalOpen(false);
            setNewPlanName('');
            return createdPlan.id;
        } catch (error) {
            console.error('Error creating development plan:', error);
            // setErrorMessage('An error occurred while creating the development plan');
        }
        return null;
    };

    // If it has a template, then it will create a new plan with the template.
    // Otherwise it will do the default behavior
    const handleCreatePlanRoute = async () => {
        const newDevPlanID = await handleCreateNewPlan();

        if (selectedTemplate === '') return;

        const sourceFileName = `template_GLWA_Aug16_${selectedTemplate}`;
        const targetFileName = `GLWA_Aug16_${userData.id}_${newDevPlanID}`;

        setNewPlanLoading(true);

        // This api call literally just copies the source dev plan file to the target.
        await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/duplicateDevPlan/${sourceFileName}/${targetFileName}`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json',}
        })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', data);
            })
            .catch((error) => {
                console.error('Error:', error);
            });

        // Refetch the dev plan content from here.
        setDevPlanContent({});

        const response = await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/${targetFileName}`);
        if (response.ok) {
            const data = await response.json();
            if (!data.message) {
                setDevPlanContent(data);

            }
        }

        // Have to do this manually because it doesn't recheck right away
        setNewPlanLoading(false);
    };

    const handleDuplicatePlan = async () => {
        setIsCopyPlanModalOpen(false);
        const selectedPlan = userData?.developmentPlans?.items?.find(plan => plan.id === devPlanID);
        console.log("handleDuplicatePlan");
        console.log("Current plan to copy: ", selectedPlan.name);
        console.log("new plan name: ", newPlanName);

        // Make a deep copy of the current devPlanContent before creating the new one.
        const currentID = devPlanID;
        const copyDevPlanContent = JSON.parse(JSON.stringify(devPlanContent));
        const newDevPlanID = await handleCreateNewPlan();

        const sourceFileName = `GLWA_Aug16_${userData.id}_${currentID}`;
        const targetFileName = `GLWA_Aug16_${userData.id}_${newDevPlanID}`;

        setNewPlanLoading(true);

        // This api call literally just copies the source dev plan file to the target.
        await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/duplicateDevPlan/${sourceFileName}/${targetFileName}`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json',}
        })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', data);
            })
            .catch((error) => {
                console.error('Error:', error);
            });

        // Have to do this manually because it doesn't recheck right away
        setDevPlanContent(copyDevPlanContent);
        setNewPlanLoading(false);
        
    };

    const handleDuplicatePlanClick = (event, plan) => {
        if (!(Object.keys(devPlanContent).length > 0)) {
            console.error('Cannot duplicate plan: No plan content is available');
            alert('Cannot duplicate plan: No plan content is available');
            return;
        }
        setNewPlanName(plan.name.concat(" copy"));
        setIsCopyPlanModalOpen(true);
    }

    const renderValue = (selected) => {
        try {
            const selectedPlan = userData?.developmentPlans?.items?.find(plan => plan.id === selected);
            return selectedPlan ? selectedPlan.name : '';
        }
        catch (error) {
            console.error('Error rendering development plan value:', error);
            return '';
        }
    };

    const FLOODING_ENABLED = process.env.REACT_APP_ENABLE_FLOODING_FEATURES === 'TRUE';
    useEffect(() => {
        if (!FLOODING_ENABLED) return;
        const fetchFloodingData = async (viewBaseline, colorScheme, storm, user, plan) => {
            if (!user || !user.id) return;
            if (!plan) return;
            if (!FLOODING_ENABLED) return;

            try {
                if (!selectedStormOption) return;
                if (!colorScheme || colorScheme === "none") return;
                if (viewBaseline) return;

                setLoadingFloodMap(true);


                // if (!containPlan(storm, user.id, plan) || plan === 10) { // When a new plan is created, no flooding map is shown
                //     resetFlBaseFlValues();
                //     setLoadingFloodMap(false);
                //     return;
                // }

                resetFlBaseFlValues();
                const dev_plan = `${user.id}_${plan}`;

                let url = "";
                if (colorScheme === "flooding") { 
                    if (!containPlan(storm, user.id, plan) || plan === 10) { // New plan or hasn't run the model yet
                        url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/getFileFromS3_tycoon_bucket/Aug16/og/avgFlooding`;
                    } else {
                        url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/getFileFromS3_tycoon_bucket/${storm}/${dev_plan}/avgFlooding`;
                    }
                } else if (colorScheme === "basementFlooding") {  // New plan or hasn't run the model yet
                    if (!containPlan(storm, user.id, plan) || plan === 10) {
                        url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/getFileFromS3_tycoon_bucket/Aug16/og/basementFlooding`;
                    } else {
                        url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/getFileFromS3_tycoon_bucket/${storm}/${dev_plan}/basementFlooding`;
                    }
                } else {
                    setLoadingFloodMap(false);
                    return;
                }

                let response = await fetch(url);
                if (!response.ok) {
                    // If the response was 404 then the file doesn't exist. Try calling the upload endpoint.
                    if (response.status === 404) {
                        // This is only for backwards compatability. All future model runs will automatically generate this file.
                        console.log("Flooding file not found. Attempting to upload file...");
                        try {
                            console.log("* UPLOAD Basement Flooding : ", dev_plan);
                            const endpoint = colorScheme === "basementFlooding" ? "upload_geojson_basement_flooding" : "upload_geojson_avgFlooding";
                            const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/${endpoint}/${storm}/${dev_plan}`;
                            const response = await fetch(url);
                            if (!response.ok) throw new Error(`2 Failed to upload GeoJSON ${storm}/${dev_plan} data from S3.`);
                        } catch (error) {
                            throw new Error(`* * Failed to upload GeoJSON ${storm}/${dev_plan} data from S3.`);
                        }
                        // Try fetching again
                        response = await fetch(url);
                        if (!response.ok) throw new Error(`3 Failed to upload GeoJSON ${storm}/${dev_plan} data from S3.`);

                    }
                    setLoadingFloodMap(false);
                    throw new Error(`Failed to fetch ${colorScheme} data from S3.`);
                }

                const data = await response.json();
                const s3Url = data.url;

                const fileResponse = await fetch(s3Url);
                if (!fileResponse.ok) {
                    setLoadingFloodMap(false);
                    throw new Error(`Failed to fetch ${colorScheme} file from S3`);
                }

                console.log("geoJSONData: ", geoJSONData);

                const new_geojsonData = await fileResponse.json();
                setGeoJSONData((prevData) => ({
                    ...prevData,
                    subcatchments: new_geojsonData,
                }));

                setLoadingFloodMap(false);

            } catch (error) {
                console.error(`${colorScheme} API Call failed:`, error);
                setLoadingFloodMap(false);
            }
        };

        fetchFloodingData(viewBaseline, colorScheme, selectedStormOption, userData, devPlanID);
    }, [viewBaseline, colorScheme, selectedStormOption, userData, devPlanID]);



    const handleColorSchemeChange = async (event) => {
        setColorScheme(event.target.value);
        setLIDMode(false);
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                gap: '20px'
            }}>

            {false && <StormDateDropDown />}

            <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <InputLabel id="map-layer-label">Map Overlay</InputLabel>
                <Select
                    labelId="map-layer-label"
                    value={colorScheme}
                    onChange={handleColorSchemeChange}
                    label="Map Layer"
                    renderValue={(selected) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LayersIcon />
                            <span>{
                                selected === 'standard' ? 'None' : 
                                selected === 'percent_impervious' ? 'Percent Impervious' :
                                selected === 'elevation' ? 'Elevation' :
                                selected === 'soil_type' ? 'Soil Type' :
                                selected === 'flooding' ? 'Flood Risk' :
                                selected === 'basementFlooding' ? 'Basement Flooding Risk' :
                                'Estimated Drainage'
                            }</span>
                        </div>
                    )}
                >
                    <MenuItem value="standard">None</MenuItem>
                    <Divider />
                    <MenuItem value="percent_impervious">Percent Impervious</MenuItem>
                    <MenuItem value="elevation">Elevation</MenuItem>
                    <MenuItem value="soil_type">Soil Type</MenuItem>
                    <MenuItem value="drawdown">Estimated Drainage</MenuItem>
                    {
                        FLOODING_ENABLED &&
                        <MenuItem value="flooding">Flood Risk</MenuItem>
                    }
                    {
                        FLOODING_ENABLED &&
                        <MenuItem value="basementFlooding">Basement Flooding Risk</MenuItem>
                    }



                </Select>
            </FormControl>

            <FormControl variant="standard" sx={{ minWidth: 200 }}>
                <InputLabel id="development-plan-select-label">Plan</InputLabel>
                <Select
                    key={devPlanID}
                    labelId="development-plan-select-label"
                    id="development-plan-select"
                    value={devPlanID}
                    onChange={handleChange}
                    renderValue={(selected) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <PlumbingIcon />
                            {renderValue(selected)}
                        </div>
                    )}
                    label="Development Plan"
                >
                    {userData && userData.developmentPlans && userData.developmentPlans.items && userData.developmentPlans.items.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {planRunStatus[plan.id] ? (
                                        <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} />
                                    ) : (
                                        <EditIcon fontSize="small" style={{ color: '#2196f3' }} />
                                    )}
                                    {plan.name}
                                </div>
                                <div>
                                    {devPlanID === plan.id && (
                                        <Tooltip title="Duplicate Plan" placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={(event) => handleDuplicatePlanClick(event, plan)}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {devPlanID === plan.id && (
                                        <Tooltip title="Delete" placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={(event) => handleDeleteClick(event, plan)}
                                                tooltip="Delete Plan"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </MenuItem>
                    ))}
                    <MenuItem value="create_new" sx={{ color: 'primary.main' }}>
                        <AddIcon sx={{ mr: 1 }} />
                        Create New Plan
                    </MenuItem>
                </Select>
            </FormControl>

            <Dialog open={isNewPlanModalOpen} onClose={() => setIsNewPlanModalOpen(false)}>
                <DialogTitle>Create New Development Plan</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Plan Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={newPlanName}
                        onChange={handleNewPlanNameChange}
                        error={!!nameError}
                        helperText={nameError}
                    />
                    <FormControl fullWidth variant="standard" sx={{mt: 1}}>
                        <InputLabel>Template (Optional)</InputLabel>
                        <Select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                        >
                            {TEMPLATE_DEV_PLANS.map((template) => (
                                <MenuItem key={template} value={template}>
                                    {template}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelNewPlan}>Cancel</Button>
                    <Button 
                        
                        onClick={handleCreatePlanRoute}
                        disabled={!!nameError || !newPlanName.trim()}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isCopyPlanModalOpen} onClose={() => setIsCopyPlanModalOpen(false)}>
                <DialogTitle>Copy Development Plan</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="New Plan Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={newPlanName}
                        onChange={handleNewPlanNameChange}
                        error={!!nameError}
                        helperText={nameError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDuplicatePlan}
                        disabled={!!nameError || !newPlanName.trim()}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Development Plan?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the plan "{planToDelete?.name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ display: "flex", flexDirection: "column", color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={newPlanLoading}
                aria-label="Copying over development plan contents"
            >
                <CircularProgress color="inherit" marginbottom="10px"/>
                <p>Copying over development plan contents...</p>
            </Backdrop>

            {!devPlanID && (
              <Tooltip
                title={"You'll need to create a development plan before you can add LID or run the model."}
                placement={"bottom-end"}
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={2500}
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'rgba(29,40,44,0.98)', // Example: change background color
                            color: 'white',              // Example: change text color
                            borderRadius: '10px',
                            fontFamily: 'Helvetica',
                            fontWeight: '600',
                            border: '2px solid rgb(148, 207, 236)',
                            fontSize: '.9rem',        // Example: set font size (you can use '1rem', '14px', etc.)
                            '& .MuiTooltip-arrow': {    // Style the arrow to match
                                color: 'primary.main',
                            },
                        },
                    },
                }}
              >
                  <IconButton
                    color="inherit"
                    aria-label="No dev plans!"
                  >
                      <ErrorOutlineIcon color="warning"/>
                  </IconButton>
              </Tooltip>
            )}

        </div>
    );
};

export default DevelopmentPlanSelector;